<template>
  <div class="search-page">
    <div class="search-container">
      <!-- 검색 결과 헤더 -->
      <div class="search-header">
        <div class="search-info">
          <h1 class="search-title">
            <span v-if="searchQuery.keyword">"{{ searchQuery.keyword }}"</span>
            <span v-else>전체 공고</span>
          </h1>
          <p class="search-count">총 <strong>{{ filteredJobs.length }}개</strong>의 공고</p>
        </div>

        <div class="search-controls">
          <select v-model="sortBy" class="sort-select">
            <option value="latest">최신순</option>
            <option value="views">인기순</option>
            <option value="deadline">마감임박순</option>
            <option value="salary">급여높은순</option>
          </select>
        </div>
      </div>

      <!-- 활성 필터 태그 -->
      <div class="active-filters" v-if="hasActiveFilters">
        <span class="filter-tag" v-if="searchQuery.region">
          {{ searchQuery.region }}
          <button @click="removeFilter('region')">×</button>
        </span>
        <span class="filter-tag" v-if="searchQuery.industry">
          {{ searchQuery.industry }}
          <button @click="removeFilter('industry')">×</button>
        </span>
        <span class="filter-tag" v-if="searchQuery.jobType">
          {{ searchQuery.jobType }}
          <button @click="removeFilter('jobType')">×</button>
        </span>
        <span class="filter-tag" v-if="searchQuery.salaryType">
          {{ searchQuery.salaryType }}
          <button @click="removeFilter('salaryType')">×</button>
        </span>
        <button class="clear-all" @click="clearAllFilters">전체 초기화</button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>검색 중...</p>
      </div>

      <!-- 검색 결과 없음 -->
      <div v-else-if="paginatedJobs.length === 0" class="empty-state">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <h3>검색 결과가 없습니다</h3>
        <p>다른 검색어나 필터를 시도해보세요</p>
        <button class="btn-reset" @click="clearAllFilters">전체 공고 보기</button>
      </div>

      <!-- 검색 결과 -->
      <div v-else>
        <div class="job-list">
          <router-link 
            v-for="job in paginatedJobs" 
            :key="job.id"
            :to="`/jobs/${job.id}`"
            class="job-card"
          >
            <div class="job-image">
              <img 
                :src="job.thumbnail || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop'" 
                :alt="job.siteName"
                @error="handleImageError"
              />
              <div class="job-badges">
                <span v-if="job.isPremium" class="badge premium">PREMIUM</span>
                <span v-if="job.isPick" class="badge pick">PICK</span>
                <span v-if="job.isUrgent" class="badge urgent">급구</span>
                <span v-if="job.isNew" class="badge new">NEW</span>
                <span v-if="getDDay(job.deadline)" class="badge" :class="getDDayClass(job.deadline)">
                  {{ getDDayText(job.deadline) }}
                </span>
              </div>
            </div>

            <div class="job-content">
              <h3 class="job-title">{{ job.title }}</h3>
              <p class="job-site">{{ job.siteName }}</p>
              
              <div class="job-tags">
                <span class="tag">{{ job.region }}</span>
                <span class="tag">{{ job.jobType }}</span>
              </div>

              <div class="job-salary">
                {{ job.salaryType }} {{ job.salary }}
              </div>

              <div class="job-footer">
                <div class="job-meta">
                  <span class="meta-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    {{ job.views || 0 }}
                  </span>
                  <span class="meta-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                    {{ job.bookmarkCount || 0 }}
                  </span>
                </div>
                <div class="job-date">
                  {{ formatDate(job.createdAt) }}
                </div>
              </div>
            </div>
          </router-link>
        </div>

        <!-- 페이지네이션 -->
        <div v-if="totalPages > 1" class="pagination">
          <button 
            class="page-btn prev" 
            :disabled="currentPage === 1"
            @click="prevPage"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>

          <button
            v-for="page in pageNumbers"
            :key="page"
            class="page-btn"
            :class="{ 
              active: page === currentPage,
              dots: page === '...'
            }"
            :disabled="page === '...'"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>

          <button 
            class="page-btn next" 
            :disabled="currentPage === totalPages"
            @click="nextPage"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useJobs } from '@/composables/useJobs'

export default {
  name: 'SearchView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const { getJobs, loading } = useJobs()

    const allJobs = ref([])
    const sortBy = ref('latest')
    const currentPage = ref(1)
    const itemsPerPage = ref(20)
    
    const searchQuery = ref({
      keyword: '',
      region: '',
      industry: '',
      jobType: '',
      salaryType: ''
    })

    // URL 쿼리에서 검색 조건 로드
    const loadFromQuery = () => {
      searchQuery.value = {
        keyword: route.query.keyword || '',
        region: route.query.region || '',
        industry: route.query.industry || '',
        jobType: route.query.jobType || '',
        salaryType: route.query.salaryType || ''
      }
      sortBy.value = route.query.sortBy || 'latest'
    }

    // 검색 조건을 URL에 반영
    const updateQuery = () => {
      const query = {}
      if (searchQuery.value.keyword) query.keyword = searchQuery.value.keyword
      if (searchQuery.value.region) query.region = searchQuery.value.region
      if (searchQuery.value.industry) query.industry = searchQuery.value.industry
      if (searchQuery.value.jobType) query.jobType = searchQuery.value.jobType
      if (searchQuery.value.salaryType) query.salaryType = searchQuery.value.salaryType
      if (sortBy.value !== 'latest') query.sortBy = sortBy.value

      router.push({ query })
    }

    // 공고 로드
    const loadJobs = async () => {
      try {
        allJobs.value = await getJobs()
      } catch (e) {
        console.error('공고 로드 실패:', e)
      }
    }

    // 필터링
    const filteredJobs = computed(() => {
      let jobs = allJobs.value

      // 키워드 검색 (최대한 넓게!)
      if (searchQuery.value.keyword) {
        const keyword = searchQuery.value.keyword.toLowerCase()
        jobs = jobs.filter(job => 
          job.title?.toLowerCase().includes(keyword) ||           // 제목
          job.siteName?.toLowerCase().includes(keyword) ||        // 현장명
          job.constructor?.toLowerCase().includes(keyword) ||     // 시공사
          job.developer?.toLowerCase().includes(keyword) ||       // 시행사
          job.agency?.toLowerCase().includes(keyword) ||          // 대행사
          job.trust?.toLowerCase().includes(keyword) ||           // 신탁사
          job.address?.toLowerCase().includes(keyword) ||         // 주소
          job.region?.toLowerCase().includes(keyword) ||          // 지역
          job.industry?.toLowerCase().includes(keyword) ||        // 업종
          job.jobType?.toLowerCase().includes(keyword) ||         // 직종
          job.description?.toLowerCase().includes(keyword) ||     // 상세설명
          job.contactName?.toLowerCase().includes(keyword) ||     // 담당자
          job.benefits?.toLowerCase().includes(keyword)           // 근무후생
        )
      }

      // 지역 필터
      if (searchQuery.value.region) {
        jobs = jobs.filter(job => job.region === searchQuery.value.region)
      }

      // 업종 필터
      if (searchQuery.value.industry) {
        jobs = jobs.filter(job => job.industry === searchQuery.value.industry)
      }

      // 직종 필터
      if (searchQuery.value.jobType) {
        jobs = jobs.filter(job => job.jobType === searchQuery.value.jobType)
      }

      // 급여 필터
      if (searchQuery.value.salaryType) {
        jobs = jobs.filter(job => job.salaryType === searchQuery.value.salaryType)
      }

      return jobs
    })

    // 정렬 (단일 정렬로 모든 우선순위 처리)
    const sortedJobs = computed(() => {
      const jobs = [...filteredJobs.value]

      return jobs.sort((a, b) => {
        // 1순위: 프리미엄 (무조건 최우선!)
        if (a.isPremium && !b.isPremium) return -1
        if (!a.isPremium && b.isPremium) return 1
        
        // 2순위: 분쟁PICK
        if (a.isPick && !b.isPick) return -1
        if (!a.isPick && b.isPick) return 1
        
        // 3순위: 급구 (프리미엄 동일한 경우)
        if (a.isUrgent && !b.isUrgent) return -1
        if (!a.isUrgent && b.isUrgent) return 1
        
        // 4순위: 사용자 선택 정렬
        switch (sortBy.value) {
          case 'views':
            return (b.views || 0) - (a.views || 0)
          
          case 'deadline': {
            if (!a.deadline) return 1
            if (!b.deadline) return -1
            const dateA = a.deadline.toDate ? a.deadline.toDate() : new Date(a.deadline)
            const dateB = b.deadline.toDate ? b.deadline.toDate() : new Date(b.deadline)
            return dateA - dateB
          }
          
          case 'salary': {
            const salaryA = extractNumber(a.salary)
            const salaryB = extractNumber(b.salary)
            return salaryB - salaryA
          }
          
          case 'latest':
          default: {
            const createdA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt)
            const createdB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt)
            return createdB - createdA
          }
        }
      })
    })

    // 페이지네이션
    const totalPages = computed(() => {
      return Math.ceil(sortedJobs.value.length / itemsPerPage.value)
    })

    const paginatedJobs = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value
      const end = start + itemsPerPage.value
      return sortedJobs.value.slice(start, end)
    })

    const pageNumbers = computed(() => {
      const pages = []
      const total = totalPages.value
      const current = currentPage.value
      
      if (total <= 7) {
        // 7페이지 이하: 전부 표시
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        // 7페이지 초과: 현재 페이지 중심으로 표시
        if (current <= 4) {
          // 앞쪽: 1 2 3 4 5 ... 10
          for (let i = 1; i <= 5; i++) pages.push(i)
          pages.push('...')
          pages.push(total)
        } else if (current >= total - 3) {
          // 뒤쪽: 1 ... 6 7 8 9 10
          pages.push(1)
          pages.push('...')
          for (let i = total - 4; i <= total; i++) pages.push(i)
        } else {
          // 중간: 1 ... 4 5 6 ... 10
          pages.push(1)
          pages.push('...')
          for (let i = current - 1; i <= current + 1; i++) pages.push(i)
          pages.push('...')
          pages.push(total)
        }
      }
      
      return pages
    })

    const goToPage = (page) => {
      if (page === '...' || page < 1 || page > totalPages.value) return
      currentPage.value = page
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }

    // 숫자 추출 (급여 정렬용)
    const extractNumber = (str) => {
      if (!str) return 0
      const match = str.match(/\d+/)
      return match ? parseInt(match[0]) : 0
    }

    // D-Day 계산
    const getDDay = (deadline) => {
      if (!deadline) return null
      
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      
      const deadlineDate = deadline.toDate ? deadline.toDate() : new Date(deadline)
      deadlineDate.setHours(0, 0, 0, 0)
      
      const diffTime = deadlineDate - today
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      return diffDays
    }

    const getDDayText = (deadline) => {
      const dDay = getDDay(deadline)
      if (dDay === null) return ''
      if (dDay === 0) return 'D-DAY'
      if (dDay < 0) return '마감'
      return `D-${dDay}`
    }

    const getDDayClass = (deadline) => {
      const dDay = getDDay(deadline)
      if (dDay === null) return ''
      if (dDay === 0) return 'dday'
      if (dDay < 0) return 'closed'
      if (dDay <= 3) return 'urgent'
      if (dDay <= 7) return 'warning'
      return 'normal'
    }

    const hasActiveFilters = computed(() => 
      searchQuery.value.keyword || 
      searchQuery.value.region || 
      searchQuery.value.industry || 
      searchQuery.value.jobType || 
      searchQuery.value.salaryType
    )

    const handleSearch = (filters) => {
      searchQuery.value = { ...filters }
      currentPage.value = 1  // 검색 시 1페이지로
      updateQuery()
    }

    const removeFilter = (key) => {
      searchQuery.value[key] = ''
      currentPage.value = 1  // 필터 제거 시 1페이지로
      updateQuery()
    }

    const clearAllFilters = () => {
      searchQuery.value = {
        keyword: '',
        region: '',
        industry: '',
        jobType: '',
        salaryType: ''
      }
      sortBy.value = 'latest'
      currentPage.value = 1  // 전체 초기화 시 1페이지로
      updateQuery()
    }

    const formatDate = (date) => {
      if (!date) return '-'
      if (date.toDate) {
        date = date.toDate()
      }
      const d = new Date(date)
      const now = new Date()
      const diff = now - d
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      
      if (days === 0) return '오늘'
      if (days === 1) return '어제'
      if (days < 7) return `${days}일 전`
      if (days < 30) return `${Math.floor(days / 7)}주 전`
      return d.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })
    }

    const handleImageError = (e) => {
      e.target.src = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop'
    }

    // 정렬 변경 시 1페이지로
    watch(sortBy, () => {
      currentPage.value = 1
      updateQuery()
    })

    // URL 쿼리 변경 감지
    watch(() => route.query, loadFromQuery)

    onMounted(() => {
      loadFromQuery()
      loadJobs()
    })

    return {
      loading,
      searchQuery,
      sortBy,
      filteredJobs,
      sortedJobs,
      paginatedJobs,
      currentPage,
      totalPages,
      pageNumbers,
      hasActiveFilters,
      handleSearch,
      removeFilter,
      clearAllFilters,
      goToPage,
      prevPage,
      nextPage,
      getDDay,
      getDDayText,
      getDDayClass,
      formatDate,
      handleImageError
    }
  }
}
</script>

<style scoped>
.search-page {
  min-height: 100vh;
  background: #f8f9fa;
}

.search-container {
  max-width: 1120px;
  margin: 0 auto;
  padding: 32px 24px 80px;
}

/* 검색 헤더 */
.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e9ecef;
}

.search-info {
  flex: 1;
}

.search-title {
  font-size: 28px;
  font-weight: 800;
  color: #191f28;
  margin-bottom: 8px;
}

.search-title span {
  color: #3182f6;
}

.search-count {
  font-size: 15px;
  color: #6b7684;
}

.search-count strong {
  color: #3182f6;
  font-weight: 700;
}

.search-controls {
  display: flex;
  gap: 12px;
}

.sort-select {
  padding: 12px 36px 12px 16px;
  background: #fff;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  color: #191f28;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238b95a1' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  transition: all 0.15s;
}

.sort-select:hover {
  border-color: #3182f6;
}

/* 활성 필터 */
.active-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
  padding: 16px;
  background: #fff;
  border-radius: 12px;
}

.filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #e8f3ff;
  color: #1971c2;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
}

.filter-tag button {
  padding: 0;
  width: 18px;
  height: 18px;
  background: rgba(25, 113, 194, 0.2);
  border: none;
  border-radius: 50%;
  color: #1971c2;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s;
}

.filter-tag button:hover {
  background: rgba(25, 113, 194, 0.3);
}

.clear-all {
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #e5e8eb;
  border-radius: 8px;
  color: #6b7684;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
}

.clear-all:hover {
  border-color: #e03131;
  color: #e03131;
}

/* 로딩 */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 20px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #f2f4f6;
  border-top-color: #3182f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 결과 없음 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
  color: #8b95a1;
}

.empty-state svg {
  opacity: 0.3;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 700;
  color: #4e5968;
}

.empty-state p {
  font-size: 15px;
  color: #8b95a1;
}

.btn-reset {
  margin-top: 8px;
  padding: 14px 28px;
  background: #3182f6;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-reset:hover {
  background: #1b64da;
}

/* 공고 리스트 */
.job-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 24px;
}

.job-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s;
  border: 1px solid transparent;
}

.job-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: #e5e8eb;
}

.job-image {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #f8f9fa;
}

.job-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.job-badges {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.badge {
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge.premium {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.badge.pick {
  background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
  color: #fff;
}

.badge.urgent {
  background: #e03131;
  color: #fff;
}

.badge.new {
  background: #12b886;
  color: #fff;
}

.badge.dday {
  background: #e03131;
  color: #fff;
  animation: pulse 1.5s infinite;
}

.badge.closed {
  background: #868e96;
  color: #fff;
}

.badge.warning {
  background: #fd7e14;
  color: #fff;
}

.badge.normal {
  background: #3182f6;
  color: #fff;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.job-content {
  padding: 20px;
}

.job-title {
  font-size: 18px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
}

.job-site {
  font-size: 14px;
  color: #6b7684;
  margin-bottom: 12px;
}

.job-tags {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
}

.tag {
  padding: 6px 12px;
  background: #f8f9fa;
  color: #4e5968;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
}

.job-salary {
  font-size: 16px;
  font-weight: 700;
  color: #1971c2;
  margin-bottom: 16px;
}

.job-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #f1f3f5;
}

.job-meta {
  display: flex;
  gap: 12px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #8b95a1;
}

.meta-item svg {
  color: #adb5bd;
}

.job-date {
  font-size: 13px;
  color: #8b95a1;
}

/* 페이지네이션 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 48px;
  padding: 32px 0;
}

.page-btn {
  min-width: 40px;
  height: 40px;
  padding: 0 12px;
  background: #fff;
  border: 1px solid #e5e8eb;
  border-radius: 8px;
  color: #4e5968;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-btn:hover:not(:disabled):not(.dots) {
  border-color: #3182f6;
  color: #3182f6;
  background: #f0f7ff;
}

.page-btn.active {
  background: #3182f6;
  border-color: #3182f6;
  color: #fff;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-btn.dots {
  border: none;
  background: transparent;
  cursor: default;
  min-width: 32px;
}

.page-btn.prev,
.page-btn.next {
  padding: 0;
  width: 40px;
}

.page-btn.prev svg,
.page-btn.next svg {
  color: currentColor;
}

/* 반응형 */
@media (max-width: 768px) {
  .search-container {
    padding: 24px 16px 60px;
  }

  .search-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .search-title {
    font-size: 24px;
  }

  .sort-select {
    width: 100%;
  }

  .job-list {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .pagination {
    gap: 4px;
    margin-top: 32px;
  }

  .page-btn {
    min-width: 36px;
    height: 36px;
    padding: 0 8px;
    font-size: 13px;
  }

  .page-btn.prev,
  .page-btn.next {
    width: 36px;
  }
}

@media (max-width: 480px) {
  .search-container {
    padding: 20px 12px 50px;
  }

  .search-title {
    font-size: 20px;
  }

  .active-filters {
    padding: 12px;
  }

  .pagination {
    gap: 3px;
    margin-top: 24px;
    padding: 24px 0;
  }

  .page-btn {
    min-width: 32px;
    height: 32px;
    padding: 0 6px;
    font-size: 12px;
  }

  .page-btn.prev,
  .page-btn.next {
    width: 32px;
  }
}
</style>